// Story #5648 lane B3 — contract row C4, Figure 15's "Linked aggregator"
// panel. The two-sided link with its authority AND its exit stated
// together — a link whose exit is not on the same screen is a trap.
import { useState } from 'react'
import type { LinkedAggregator } from './types'

interface LinkedAggregatorPanelProps {
  link: LinkedAggregator | null
  onUnlink: () => void
  unlinking: boolean
  error?: string | null
}

export default function LinkedAggregatorPanel({ link, onUnlink, unlinking, error }: LinkedAggregatorPanelProps) {
  const [confirming, setConfirming] = useState(false)

  const handleUnlinkClick = () => {
    if (!confirming) { setConfirming(true); return }
    setConfirming(false)
    onUnlink()
  }

  return (
    <div data-part="LinkedAggregatorPanel" className="light-card p-4 lg:p-5">
      {/* The same small uppercase blue section heading the Vocabulary, Encryption
          key policy and Member defaults cards use directly beneath it — this is
          one column of cards, and one card wearing a different heading reads as
          a card that wandered in from somewhere else. */}
      <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-iflow-700">
        Linked aggregator
      </h3>

      {link ? (
        <div className="flex items-center justify-between gap-2 py-1 border-t border-gray-100 first:border-t-0">
          <span className="text-sm text-gray-700">{link.aggregator_name}</span>
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Linked</span>
        </div>
      ) : (
        <p className="text-xs text-gray-500 py-1">This organisation has no linked aggregator.</p>
      )}

      <p className="text-xs text-gray-600 leading-relaxed mt-2">
        <strong>{'Either side can propose a link and either side can end it alone.'}</strong>{' '}
        {"A proposal from that account's billing owner appears here for an admin to accept, "
          + 'and a proposal you send appears there for them.'}
      </p>

      {error && (
        <div role="alert" className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mt-3">
        {link && (
          <button
            type="button"
            onClick={handleUnlinkClick}
            disabled={unlinking}
            title={"Unlinking removes this organisation's vocabulary and policy from that workspace "
              + 'on their next request. It changes no data and revokes no access — nobody loses a '
              + 'project, a grant or a document.'}
            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {confirming ? 'Confirm unlink' : 'Unlink'}
          </button>
        )}
        <button
          type="button"
          disabled
          title={link
            ? 'Available when this organisation is unlinked'
            : 'Proposing a link is not available yet — the two-sided proposal handshake is not built'}
          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-400 opacity-50 cursor-not-allowed"
        >
          Propose a link →
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-2">
        {"A platform administrator also satisfies this organisation's side of the handshake."}
      </p>
    </div>
  )
}
